import angular from 'angular';
import textOverflowDirective from './text-overflow-directive.js';
import standaloneDirective from './standalone-directive.js';
import escapeHtml from './escape-html-service.js';
import nlToBr from './nl-to-br-service.js';

/**
 * @ngdoc module
 * @name akamai.components.utils
 * @description Utility methods for akamai components
 *
 * @example index.html
 * <span akam-text-overflow="vm.somethingWithALongValue">{{vm.somethingWithALongValue}}</span>
 *
 * <akam-table akam-standalone></akam-table>
 */
export default angular.module('akamai.components.utils', [])

/**
 * @ngdoc directive
 *
 * @name akamTextOverflow
 *
 * @description will add a html tooltip if needed to some text
 *
 * @restrict A
 *
 * @param {string} akamTextOverflow Should be set to the value that
 * should be presented in a tooltip if needed. It default watches
 * this value so that if it changes we will update it. To avoid this
 * we recommend one time binding any text that does not change to prevent
 * additional watches.
 *
 */
  .directive('akamTextOverflow', textOverflowDirective)
/**
 * @ngdoc directive
 *
 * @name akamStandalone
 *
 * @description adds an css class (standalone) to the element
 *
 * @restrict A
 *
 */
  .directive('akamStandalone', standaloneDirective)

/* eslint-disable max-len */
/**
 * @ngdoc service
 * @name escapeHtml
 * @description Escapes untrusted HTML by HTML-encoding certain characters. See:
 * https://www.owasp.org/index.php/XSS_(Cross_Site_Scripting)_Prevention_Cheat_Sheet#RULE_.231_-_HTML_Escape_Before_Inserting_Untrusted_Data_into_HTML_Element_Content
 * Encodes &, <, >, ", ', /
 *
 * Normally, you would simply use {{user_text}} which already encodes any HTML characters
 * by using element.innerText.
 *
 * However, there is no way to do it programmatically, which you need to do if you want to
 * manipulate the text by adding HTML to it (like adding paragraph or anchor tags). The closest
 * is the <code>$sanitize</code> service, which does not encode HTML which it considers safe.
 * Use this along with ng-bind-html if you want to ensure that user text will not affect the DOM.
 *
 * See also the example here: https://docs.angularjs.org/api/ng/service/$sce
 *
 * @param html {string} the string to escape
 *
 */
  .factory('escapeHtml', escapeHtml)
/* eslint-enable max-len */

/**
 * @ngdoc filter
 * @name nlToBr
 * @description Take untrusted text, convert it into escaped HTML,
 * and convert \n newlines into &lt;br&gt; tags.  See escapeHtml service.
 *
 * If you simply need to print user-text while retaining line breaks, use the util-pre-wrap class
 * instead, which applies the white-space: pre-wrap style.
 *
 * @example <div ng-bind-html="the\nquick\nbrown fox < wolf"|nlToBr"></div>
 * Outputs: `"the<br>quick<br>brown fox &lt; wolf"`
 *
 */
  .filter('akamNlToBr', nlToBr)

/**
 * @ngdoc service
 * @name nlToBr
 * @description Take untrusted text, convert it into escaped HTML,
 * and convert \n newlines into &lt;br&gt; tags.  See escapeHtml service.
 *
 * @example nlToBr("the\nquick\nbrown fox < wolf")
 * Outputs: `"the<br>quick<br>brown fox &lt; wolf"`
 *
 */
  .factory('nlToBr', nlToBr);
